import { useTranslation } from 'i18n';
import { formatNumber, Stack } from 'ui';

import { formatPercentageFeature } from '../../utils/formatFeature/formatFeature';
import { MaterialBreakdownItem } from '../MaterialBreakdownItem/MaterialBreakdownItem';
import { MaterialSampleBreakdownItem } from '../MaterialSampleBreakdownItem/MaterialSampleBreakdownItem';

const ZORBA_SUM_KEYS: string[] = [
  "Non-ferrous Metal 'whites'",
  "Copper & Brass 'reds & yellows'",
  'Post-consumer Aluminium Packaging',
  'Composite Materials',
  'Aluminium - UBC',
];

const NON_ZORBA_SUM_KEYS: string[] = ['Other Metal', 'Non-Metal', 'Prohibitive', 'Ferrous metal', 'Fines <20mm'];

export const ZorbaMaterialSampleBreakdown = ({
  totalWeight,
  breakdown,
  precision = 1,
}: {
  totalWeight: number;
  breakdown: Record<string, number>;
  precision?: number;
}) => {
  const { t } = useTranslation('features');
  const totalZorba = ZORBA_SUM_KEYS.reduce((acc, key) => acc + (breakdown[key] ?? 0), 0);
  const totalNonZorba = NON_ZORBA_SUM_KEYS.reduce((acc, key) => acc + (breakdown[key] ?? 0), 0);

  const otherMetal = (breakdown['Ferrous metal'] || 0) + (breakdown['Other Metal'] || 0);

  return (
    <Stack
      sx={{
        gap: 2,
      }}
    >
      <div>
        <MaterialBreakdownItem
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.targetMaterial')}
          variant="bold"
          value={formatNumber(totalZorba, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={formatPercentageFeature(totalZorba / totalWeight)}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown["Non-ferrous Metal 'whites'"]}
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.nonFerrousMetalWhites')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown["Copper & Brass 'reds & yellows'"]}
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.copperBrassRedsYellows')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Post-consumer Aluminium Packaging']}
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.postConsumerAluminiumPackaging')}
          totalWeight={totalWeight}
        />

        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Composite Materials']}
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.compositeMaterials')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Aluminium - UBC']}
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.aluminiumUbc')}
          totalWeight={totalWeight}
        />
      </div>
      <div>
        <MaterialBreakdownItem
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.nonTarget')}
          variant="bold"
          value={formatNumber(totalNonZorba, {
            maximumFractionDigits: precision,
            minimumFractionDigits: precision,
            style: 'kilogram',
          })}
          secondaryValue={formatPercentageFeature(totalNonZorba / totalWeight)}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={otherMetal}
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.otherMetalFerrous')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Non-Metal']}
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.otherNonMetal')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown['Fines <20mm']}
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.finesLt20mm')}
          totalWeight={totalWeight}
        />
        <MaterialSampleBreakdownItem
          precision={precision}
          value={breakdown.Prohibitive}
          label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.prohibitive')}
          totalWeight={totalWeight}
        />
      </div>
      <MaterialBreakdownItem
        label={t('breakdowns.zorbaMaterialSampleBreakdown.labels.total')}
        value={formatNumber(totalWeight, {
          maximumFractionDigits: precision,
          minimumFractionDigits: precision,
          style: 'kilogram',
        })}
        secondaryValue={'100%'}
        variant="bold"
      />
    </Stack>
  );
};
